// src/pages/Admin/Appointment/AllPetSurgery.jsx
import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import { FaTimes, FaBell, FaCheckCircle, FaBan, FaClock, FaUserMd, FaPaw, FaStethoscope, FaNotesMedical } from "react-icons/fa";
import Swal from 'sweetalert2';
import '../App/modalstyle.css';

export default function AllPetSurgery() {
    const { t } = useContext(TranslatorContext);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    const [petSurgeries, setPetSurgeries] = useState([]);
    const [surgeries, setSurgeries] = useState([]);
    const [loading, setLoading] = useState(true);

    // Edit Modal States
    const [editModal, setEditModal] = useState(false);
    const [selectedSurgery, setSelectedSurgery] = useState(null);
    const [editFormData, setEditFormData] = useState({
        appointment_id: '',
        surgery_id: '',
        note: ''
    });
    const [updatingSurgery, setUpdatingSurgery] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        customer_name: '',
        pet_name: '',
        surgery_id: ''
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(10);

    useEffect(() => {
        fetchPetSurgeries();
    }, [currentPage, filters]);

    const fetchPetSurgeries = async () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        const params = new URLSearchParams({
            page: currentPage,
            ...(filters.customer_name && { customer_name: filters.customer_name }),
            ...(filters.pet_name && { pet_name: filters.pet_name }),
            ...(filters.surgery_id && { surgery_id: filters.surgery_id })
        });

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/all-pet-surgery?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setPetSurgeries(response.data.petSurgeries.data || []);
                setSurgeries(response.data.surgeries || []);
                setLastPage(response.data.petSurgeries.last_page || 1);
                setTotal(response.data.petSurgeries.total || 0);
                setPerPage(response.data.petSurgeries.per_page || 10);
            }
        } catch (error) {
            console.error("Error fetching pet surgeries:", error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('failed to load surgeries'),
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            customer_name: '',
            pet_name: '',
            surgery_id: ''
        });
        setCurrentPage(1);
    };

    const openEditModal = (surgery) => {
        setSelectedSurgery(surgery);
        setEditFormData({
            appointment_id: surgery.appointment_id || '',
            surgery_id: surgery.surgery_id || '',
            note: surgery.note || surgery.note?.note || ''
        });
        setEditModal(true);
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSurgeryUpdate = async () => {
        if (!editFormData.appointment_id) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('please select appointment'),
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        if (!editFormData.surgery_id) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('please select surgery'),
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        setUpdatingSurgery(true);
        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-surgery-update/${selectedSurgery.id}`,
                editFormData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setEditModal(false);
                setSelectedSurgery(null);
                setEditFormData({
                    appointment_id: '',
                    surgery_id: '',
                    note: ''
                });
                fetchPetSurgeries();

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('surgery updated successfully'),
                    timer: 2000,
                    showConfirmButton: true,
                    confirmButtonColor: '#3085d6'
                });
            }
        } catch (error) {
            console.error("Error updating surgery:", error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('failed to update surgery'),
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setUpdatingSurgery(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';

            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;

            const formattedDate = `${day}-${month}-${year}`;
            const formattedTime = `${hours}:${minutes} ${ampm}`;

            return `${formattedDate} ${formattedTime}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage) {
            setCurrentPage(newPage);
        }
    };

    if (loading && currentPage === 1) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">{t("loading")}...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('pet surgeries')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('appointments')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('surgeries')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Filters Section */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-body">
                            <Row>
                                <Col xl={4} className="mb-3">
                                    <Form.Label className="fw-bold">{t('customer name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="customer_name"
                                        value={filters.customer_name}
                                        onChange={handleFilterChange}
                                        placeholder={t('search by customer')}
                                    />
                                </Col>

                                <Col xl={4} className="mb-3">
                                    <Form.Label className="fw-bold">{t('pet name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="pet_name"
                                        value={filters.pet_name}
                                        onChange={handleFilterChange}
                                        placeholder={t('search by pet')}
                                    />
                                </Col>

                                <Col xl={4} className="mb-3">
                                    <Form.Label className="fw-bold">{t('surgery type')}</Form.Label>
                                    <Form.Select
                                        name="surgery_id"
                                        value={filters.surgery_id}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">{t('all surgeries')}</option>
                                        {surgeries.map(surgery => (
                                            <option key={surgery.id} value={surgery.id}>
                                                {surgery.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            </Row>

                            <Row>
                                <Col xl={12} className="d-flex justify-content-end">
                                    <ButtonComponent
                                        className="btn btn-secondary"
                                        onClick={clearFilters}
                                    >
                                        <FaTimes className="me-1" /> {t('clear filters')}
                                    </ButtonComponent>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>

                {/* Table Section */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header d-flex justify-content-between align-items-center">
                            <h5 className="mc-card-title mb-0">{t('surgeries list')}</h5>
                            <span className="mc-badge mc-badge-info">
                                {t('total')}: {total}
                            </span>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="mc-card">
                                    <div className="table-responsive-wrapper">
                                        <Table responsive className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mc-table-check">
                                                            <p>#</p>
                                                        </div>
                                                    </th>
                                                    <th>{t('appointment number')}</th>
                                                    <th>{t('pet name')}</th>
                                                    <th>{t('surgery type')}</th>
                                                    <th>{t('surgery date')}</th>
                                                    <th>{t('doctor')}</th>
                                                    <th>{t('notes')}</th>
                                                    <th>{t('actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {petSurgeries.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            {t('No surgeries found')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    petSurgeries.map((surgery, index) => (
                                                        <tr key={surgery.id}>
                                                            <td>
                                                                <div className="mc-table-check">
                                                                    <p>{(currentPage - 1) * perPage + index + 1}</p>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="mc-badge mc-badge-primary">
                                                                    {surgery.appointment?.appointment_number || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <strong className="d-flex align-items-center gap-2">
                                                                        <FaPaw className="text-primary" />
                                                                        {surgery.appointment?.pet?.name || 'N/A'}
                                                                    </strong>
                                                                    <small className="text-muted">
                                                                        {t('owner')}: {surgery.appointment?.customer?.name || 'N/A'}
                                                                    </small>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {surgery.surgery?.name || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        {formatDateTime(surgery.created_at)}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FaUserMd className="text-secondary" />
                                                                    {surgery.appointment?.doctor?.name || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FaNotesMedical className="text-info" />
                                                                    <span className="text-truncate" style={{ maxWidth: '150px' }}>
                                                                        {surgery.note?.note || surgery.note || '-'}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="mc-table-action">
                                                                    {hasPermission("Pet surgery edit") && (

                                                                        <ButtonComponent
                                                                            type="button"
                                                                            className="material-icons edit"
                                                                            onClick={() => openEditModal(surgery)}
                                                                            title={t('edit surgery')}
                                                                        >
                                                                            edit_note
                                                                        </ButtonComponent>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* Pagination */}
                                        {lastPage > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top px-3 pb-3">
                                                <div className="text-muted small">
                                                    {t('showing')} {(currentPage - 1) * perPage + 1} {t('to')} {Math.min(currentPage * perPage, total)} {t('of')} {total} {t('entries')}
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        {t('previous')}
                                                    </ButtonComponent>
                                                    <span className="px-3 py-1 bg-light rounded">
                                                        {t('page')} {currentPage} {t('of')} {lastPage}
                                                    </span>
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === lastPage}
                                                    >
                                                        {t('next')}
                                                    </ButtonComponent>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            {/* Edit Surgery Modal */}
            <Modal
                show={editModal}
                onHide={() => setEditModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        {t('edit surgery details')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={(e) => e.preventDefault()}>
                    <Modal.Body className="custom-modal-body">
                        {selectedSurgery && (
                            <>
                                <div className="alert alert-info mb-3">
                                    <div><strong>{t('appointment')}:</strong> #{selectedSurgery.appointment?.appointment_number}</div>
                                    <div><strong>{t('pet')}:</strong> {selectedSurgery.appointment?.pet?.name}</div>
                                    <div><strong>{t('surgery date')}:</strong> {formatDateTime(selectedSurgery.surgery_date)}</div>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Control
                                        type="hidden"
                                        name="appointment_id"
                                        value={editFormData.appointment_id}
                                        onChange={handleEditFormChange}
                                        placeholder={t('appointment id')}
                                        className="custom-input"
                                        disabled
                                    />
                                    {/* <small className="text-muted">{t('appointment_id_locked')}</small> */}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('surgery type')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="surgery_id"
                                        value={editFormData.surgery_id}
                                        onChange={handleEditFormChange}
                                        className="custom-input"
                                    >
                                        <option value="">{t('select surgery')}</option>
                                        {surgeries.map(surgery => (
                                            <option key={surgery.id} value={surgery.id}>
                                                {surgery.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('notes')}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="note"
                                        value={editFormData.note}
                                        onChange={handleEditFormChange}
                                        placeholder={t('enter surgery notes')}
                                        className="custom-input"
                                    />
                                </Form.Group>

                                <div className="alert alert-warning mt-3">
                                    <FaTimes className="me-2" />
                                    <small>{t('surgery_update_warning')}</small>
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setEditModal(false)}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            type="button"
                            className="btn btn-primary px-4"
                            onClick={handleSurgeryUpdate}
                            disabled={updatingSurgery}
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                        >
                            {updatingSurgery && <span className="spinner-border spinner-border-sm me-2"></span>}
                            {t('update surgery')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            <style jsx>{`
                .custom-modal-dialog {
                    max-width: 550px;
                }
                .custom-modal-content {
                    border-radius: 12px;
                }
                .custom-modal-header {
                    background-color: #d7d8da;
                    border-bottom: none;
                }
                .custom-modal-header .btn-close {
                    filter: brightness(0) invert(1);
                }
                .custom-modal-title {
                    font-weight: 600;
                }
                .custom-modal-body {
                    padding: 20px;
                }
                .custom-modal-footer {
                    border-top: none;
                    padding: 15px 20px;
                }
                .custom-input:focus {
                    border-color: #3182ce;
                    box-shadow: 0 0 0 0.2rem rgba(8, 88, 247, 0.25);
                }
                .mc-table-action {
                    display: flex;
                    gap: 8px;
                }
                .mc-table-action .material-icons {
                    cursor: pointer;
                    font-size: 20px;
                }
                .mc-table-action .material-icons.edit {
                    color: #3182ce;
                }
                .mc-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .mc-badge-primary {
                    background-color: #cfe2ff;
                    color: #084298;
                }
                .mc-badge-info {
                    background-color: #d1ecf1;
                    color: #0c5460;
                }
                textarea.custom-input {
                    resize: vertical;
                }
            `}</style>
        </PageLayout>
    );
}